import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function LicenseesPage() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    licenseNumber: '',
    yearsInBusiness: '',
    serviceAreas: '',
    equipmentOwned: '',
    insuranceCoverage: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5pm1e0tu5df1co4cvr0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          businessAddress: '',
          city: '',
          state: '',
          zipCode: '',
          licenseNumber: '',
          yearsInBusiness: '',
          serviceAreas: '',
          equipmentOwned: '',
          insuranceCoverage: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/47e827c92538301e7d8728560e34a5b6.jpg"
            alt="Join Our Network"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-7xl font-black text-white mb-6 leading-tight">
            Grow Your Demolition Business
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            Join our network of licensed professionals and access quality projects
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-black text-gray-900 mb-6 leading-tight">
              Why Partner With DemoHub?
            </h2>
            <p className="text-gray-600 text-base max-w-3xl mx-auto leading-relaxed">
              Join hundreds of successful demolition contractors who have grown their business through our platform
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-add-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Quality Leads
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Receive pre-qualified leads from property owners actively seeking demolition services in your area. No more cold calling or wasted time.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-dashboard-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Powerful CRM
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Manage all your projects, quotes, and customer communications through our intuitive dashboard. Stay organized and efficient.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-3xl text-gray-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Transparent Payments
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Simple, transparent pricing with no hidden fees. Get paid quickly and securely through our integrated payment system.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-4xl font-black text-gray-900 mb-6 leading-tight">
                Additional Benefits
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-2xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900 text-base">Marketing Support</strong>
                    <p className="text-gray-600 text-sm">We handle the marketing so you can focus on what you do best</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-2xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900 text-base">Flexible Schedule</strong>
                    <p className="text-gray-600 text-sm">Choose the projects that fit your availability and capacity</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-2xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900 text-base">Professional Network</strong>
                    <p className="text-gray-600 text-sm">Connect with other professionals and share best practices</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-teal-600 text-2xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900 text-base">24/7 Support</strong>
                    <p className="text-gray-600 text-sm">Our dedicated support team is always here to help you succeed</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=demolition%20contractor%20using%20tablet%20and%20digital%20tools%20on%20construction%20site%20modern%20technology%20in%20demolition%20industry%20professional%20worker%20with%20equipment%20digital%20transformation%20business%20management&width=700&height=600&seq=benefits1&orientation=portrait"
                alt="Benefits"
                className="w-full h-[600px] object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-black text-gray-900 mb-4 leading-tight">
                Apply to Become a Licensee
              </h2>
              <p className="text-gray-600 text-base">
                Fill out the application below and our team will review your submission
              </p>
            </div>

            <form id="licensee-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
              {/* Company Information */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="ABC Demolition Co."
                    />
                  </div>
                  <div>
                    <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="john@abcdemo.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Business Address */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">Business Address</h3>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      id="businessAddress"
                      name="businessAddress"
                      value={formData.businessAddress}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="1234 Business Blvd"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="Portland"
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="OR"
                      />
                    </div>
                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="97201"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Details */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-6">Business Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-2">
                      License Number *
                    </label>
                    <input
                      type="text"
                      id="licenseNumber"
                      name="licenseNumber"
                      value={formData.licenseNumber}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="LIC-123456"
                    />
                  </div>
                  <div>
                    <label htmlFor="yearsInBusiness" className="block text-sm font-medium text-gray-700 mb-2">
                      Years in Business *
                    </label>
                    <select
                      id="yearsInBusiness"
                      name="yearsInBusiness"
                      value={formData.yearsInBusiness}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                    >
                      <option value="">Select...</option>
                      <option value="0-2">0-2 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="6-10">6-10 years</option>
                      <option value="10+">10+ years</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="serviceAreas" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Areas *
                    </label>
                    <input
                      type="text"
                      id="serviceAreas"
                      name="serviceAreas"
                      value={formData.serviceAreas}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="Portland, Salem, Eugene"
                    />
                  </div>
                  <div>
                    <label htmlFor="insuranceCoverage" className="block text-sm font-medium text-gray-700 mb-2">
                      Insurance Coverage *
                    </label>
                    <input
                      type="text"
                      id="insuranceCoverage"
                      name="insuranceCoverage"
                      value={formData.insuranceCoverage}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="e.g., $2M General Liability"
                    />
                  </div>
                </div>
              </div>

              {/* Equipment */}
              <div>
                <label htmlFor="equipmentOwned" className="block text-sm font-medium text-gray-700 mb-2">
                  Equipment Owned *
                </label>
                <textarea
                  id="equipmentOwned"
                  name="equipmentOwned"
                  value={formData.equipmentOwned}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                  placeholder="List your demolition equipment (excavators, loaders, etc.) - max 500 characters"
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.equipmentOwned.length}/500 characters
                </p>
              </div>

              {/* Additional Info */}
              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                  placeholder="Tell us more about your company and why you'd like to join DemoHub - max 500 characters"
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">
                  {formData.additionalInfo.length}/500 characters
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-teal-600 text-white text-base font-bold rounded-lg hover:bg-teal-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">
                    Thank you for your application! Our team will review your submission and contact you within 2-3 business days.
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm font-medium">
                    There was an error submitting your application. Please try again or contact us directly.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-black text-gray-900 mb-16 text-center leading-tight">
            Success Stories
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="bg-gray-50 rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center text-white text-2xl font-black">
                  JM
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg">John Martinez</h4>
                  <p className="text-gray-600 text-sm">Precision Demo Co.</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                "Joining DemoHub was the best decision for my business. In the first year, my revenue increased by 60% and I was able to hire three additional crew members. The platform is easy to use and the leads are high quality."
              </p>
              <div className="flex items-center gap-2 text-amber-500">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center text-white text-2xl font-black">
                  SW
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-lg">Sarah Wilson</h4>
                  <p className="text-gray-600 text-sm">Metro Demolition Services</p>
                </div>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                "The CRM dashboard has streamlined our entire operation. We can manage multiple projects simultaneously and the payment processing is seamless. DemoHub has helped us scale our business efficiently."
              </p>
              <div className="flex items-center gap-2 text-amber-500">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-teal-700 to-gray-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-black text-white mb-6 leading-tight">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Join our network today and start receiving quality demolition projects
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-teal-600 text-base font-bold rounded-full hover:bg-gray-100 transition-all shadow-xl cursor-pointer whitespace-nowrap"
          >
            View Dashboard Demo
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}